import { isSendPress } from './contact'
import { emailLinks } from './mailto'
import { onAPhone } from './rebook-message'
import { api, changeNow } from './api'
import { getMe } from './me'
import { copyText } from './copied'
import { el, toast } from './ui'
import { lineDrawing } from './picture-tiles'

type EmailApp = 'gmail' | 'outlook' | 'yahoo' | 'computer' | 'phone' | 'copy'
interface Sending { email_app: EmailApp | null; confirm_computer_send: boolean }
export interface EmailSend { href: string; sent: () => void; name?: string; tab?: Window | null; confirm?: boolean }
const choices: [EmailApp, string, string][] = [
  ['gmail','Gmail','In your browser'], ['outlook','Outlook','In your browser'], ['yahoo','Yahoo Mail','In your browser'],
  ['computer','This computer','Its own mail app'], ['phone','My phone','Send it from there'], ['copy','Copy it','Paste it anywhere'],
]
let memory: Sending | null = null, owner = '', loading: Promise<Sending> | null = null
let revision = 0
const savedFields: Partial<Record<keyof Sending, number>> = {}
const identity = (): string => `${getMe()?.businessId}:${getMe()?.email}`
export function sendingSettings(): Promise<Sending> {
  const who = identity()
  if (who !== owner) { owner = who; memory = null; loading = null; revision++ }
  if (memory) return Promise.resolve(memory)
  if (loading) return loading
  const version = revision
  const request = api<Sending>('/api/me/sending').then(row => { if (identity() === who && version === revision) memory = row; return row })
  loading = request
  void request.finally(() => { if (loading === request) loading = null }).catch(() => {})
  return request
}
export async function saveSending(patch: Partial<Sending>): Promise<Sending> {
  const who = identity()
  const version = ++revision
  const fields = Object.keys(patch) as (keyof Sending)[]
  for(const field of fields)savedFields[field]=version
  const row = await changeNow<Sending>('PATCH','/api/me/sending',patch)
  if (identity() === who) {
    const current = fields.filter(field=>savedFields[field]===version)
    if(current.length) {
      owner=who
      memory={...(memory??row),...Object.fromEntries(current.map(field=>[field,row[field]]))}
    }
    return memory??row
  }
  return row
}
function popup(title: string, hint: string, cancelled: () => void): { dialog: HTMLDialogElement; body: HTMLElement; close: () => void } {
  const dialog = el('dialog',{class:'booking-dialog sending-dialog','aria-label':title}) as HTMLDialogElement
  const close = el('button',{type:'button',class:'sheet-x','aria-label':'Close'},'Close')
  const body = el('div',{class:'sending-body'})
  dialog.append(el('div',{class:'booking-head'},el('h2',{},title),close),el('p',{class:'muted sending-hint'},hint),body)
  let decided = false
  const finish = (): void => { decided = true; dialog.close() }
  close.addEventListener('click',()=>dialog.close())
  dialog.addEventListener('close',()=>{dialog.remove();if(!decided)cancelled()})
  document.body.append(dialog);dialog.showModal()
  return {dialog,body,close:finish}
}
export function chooseEmail(chosen: (app: EmailApp) => void, cancelled: () => void = () => {}): void {
  const who=identity()
  const s = popup('Which email do you use?','Tap one. Asked once; change it in Your messages.',cancelled)
  const tiles = el('div',{class:'sending-tiles'})
  for (const [value,label,hint] of choices) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg','svg')
    svg.setAttribute('viewBox','0 0 24 24');svg.setAttribute('width','26');svg.setAttribute('height','26');svg.setAttribute('aria-hidden','true')
    const p = document.createElementNS('http://www.w3.org/2000/svg','path')
    p.setAttribute('d',value==='phone'?'M7 2h10v20H7zM11 18h2':value==='computer'?'M4 5h16v11H4zM2 19h20':value==='copy'?'M8 8h12v12H8zM4 16V4h12':'M3 5h18v14H3zM3 7l9 6 9-6')
    p.setAttribute('fill','none');p.setAttribute('stroke','currentColor');p.setAttribute('stroke-width','2');p.setAttribute('stroke-linejoin','round');svg.append(p)
    const tile = el('button',{type:'button',class:`sending-tile${value===(memory?.email_app??'gmail')?' is-suggested':''}`,'data-email-app':value},svg,el('span',{},el('strong',{},label),el('small',{},hint)))
    tile.addEventListener('click',()=>{
      if(identity()!==who){s.close();return}
      // Open on this tap before awaiting persistence, so web compose is never blocked by an async save.
      memory = {...(memory??{confirm_computer_send:true}),email_app:value};owner=identity()
      s.close();chosen(value)
      void saveSending({email_app:value}).catch(()=>toast("Couldn't save your email choice. Try Change in Your messages when you have signal."))
    });tiles.append(tile)
  }
  s.body.append(tiles)
}
export function confirmComputerSend(sent: () => void, no: () => void = () => {}, hint = 'Your message opened.'): void {
  if (onAPhone()) { sent(); return }
  const who = identity()
  const show = (): void => {
    if(identity()!==who)return
    if(getMe()?.role!=='worker'&&memory?.confirm_computer_send===false){sent();return}
    const safeNo = (): void => { if(identity()===who)no() }
    const s = popup('Did it send?',hint,safeNo)
    s.dialog.classList.add('email-send-question')
    const yes = el('button',{type:'button',class:'btn-primary'},lineDrawing('tick',22),'Yes, it sent')
    const not = el('button',{type:'button',class:'btn-secondary'},'No, not sent')
    let done = false
    const finish = (ok: boolean): void => { if(done)return;done=true;s.close();if(identity()===who){if(ok)sent();else no()} }
    yes.addEventListener('click',()=>finish(true));not.addEventListener('click',()=>finish(false))
    s.body.append(el('div',{class:'email-send-answers'},yes,not),el('p',{class:'muted'},'Only "Yes" counts it as sent. "No" leaves everything where it was.'))
    yes.focus()
  }
  // Reserved sends may finish their claim before the owner's preference read. Respect the switch on that first send too.
  if(getMe()?.role==='worker'||owner===who&&memory)show()
  else void sendingSettings().then(show).catch(show)
}
// Reserve only a browser compose whose destination is already known, on the original tap.
export function reserveOwnSend(href: string): Window | null {
  const links = emailLinks(href)
  const app = owner===identity()?memory?.email_app:null
  if (links ? !(getMe()?.role==='worker'||app&&['gmail','outlook','yahoo'].includes(app)) : !href.startsWith('http')) return null
  const tab = window.open('about:blank','_blank')
  if(tab)tab.opener=null
  return tab
}
// All own-app hand-offs use the same captured draft and save boundary. Roundly sends bypass this helper.
export function openOwnSend(send: EmailSend, cancelled: () => void = () => {}, workerEmail = false): void {
  const who = identity(), links = emailLinks(send.href)
  if(owner!==who){owner=who;memory=null;loading=null;revision++}
  const cancel = (): void => { send.tab?.close();if(identity()===who)cancelled() }
  const go = (app: EmailApp | null): void => {
    if (identity() !== who) { send.tab?.close(); return }
    const safeSent = (): void => { if(identity()===who)send.sent() }
    let label = send.href.startsWith('sms:')?'Your Messages app':'WhatsApp'
    let href = send.href
    if (links && app) {
      label = choices.find(c=>c[0]===app)![1]
      const draft = new URL(links.mailto), to = decodeURIComponent(draft.pathname), subject = draft.searchParams.get('subject')??'', body = draft.searchParams.get('body')??''
      if(app==='phone'||app==='copy') {
        send.tab?.close()
        const text = `To: ${to}\nSubject: ${subject}\n\n${body}`
        const s = popup(app==='phone'?'Send it from your phone':'Copy it',app==='phone'?'Open Roundly on your phone, or copy this message to send from there.':'Paste it anywhere.',cancel)
        const words = el('textarea',{'aria-label':'Message to copy',rows:'8',readonly:''});words.value=text
        const copy = el('button',{type:'button',class:'btn-secondary'},'Copy')
        copy.addEventListener('click',()=>void copyText(text,{button:copy,what:'message'}))
        const next = el('button',{type:'button',class:'btn-primary'},'I sent it')
        next.addEventListener('click',()=>{s.close();if(send.confirm===false)safeSent();else confirmComputerSend(safeSent,cancel,app==='phone'?'Your message is ready to send from your phone.':'Your message is ready to paste and send.')})
        s.body.append(words,copy,next)
        if(app==='copy')void copyText(text,{button:copy,what:'message'})
        return
      }
      if(app==='gmail')href=links.gmail
      if(app==='computer')href=links.mailto
      if(app==='outlook')href=`https://${/@(?:outlook|hotmail|live)\.[a-z.]+$/i.test(getMe()?.email??'')?'outlook.live.com':'outlook.office.com'}/mail/0/deeplink/compose?to=${encodeURIComponent(to)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
      if(app==='yahoo')href=`https://compose.mail.yahoo.com/?to=${encodeURIComponent(to)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    }
    if(send.tab && href.startsWith('http')) { send.tab.opener=null;send.tab.location.href=href }
    else { send.tab?.close();window.open(href,href.startsWith('http')?'_blank':'_self','noopener') }
    if(send.confirm===false)safeSent();else confirmComputerSend(safeSent,cancel,`${label} opened with your message${send.name?` to ${send.name}`:''}.`)
  }
  // Workers keep native hand-offs except the bound email links that already used Gmail and confirmation.
  if (onAPhone() || getMe()?.role==='worker'&&!workerEmail) { send.tab?.close();window.open(send.href,send.href.startsWith('http')?'_blank':'_self','noopener');send.sent();return }
  if(getMe()?.role==='worker'){go(links?'gmail':null);return}
  if(!links){go(null);return}
  if (memory) { if(links&&!memory.email_app){send.tab?.close();send.tab=null;chooseEmail(go,cancel)}else go(memory.email_app);return }
  // A very fast first email tap may beat the preference read. Wait without opening an unknown destination.
  const reserved = send.tab ?? reserveOwnSend(send.href)
  if(reserved) { reserved.opener=null;send.tab=reserved }
  void sendingSettings().then(()=>{
    if(identity()!==who){reserved?.close();return}
    if(links&&!memory?.email_app){reserved?.close();send.tab=null;chooseEmail(go,cancel)}
    else go(memory?.email_app??null)
  }).catch(()=>{reserved?.close();toast('Check your signal before sending.');cancel()})
}
export function bindEmailSend(link: HTMLAnchorElement, prepare: () => EmailSend | null, settled?: () => void): void {
  if (!onAPhone() && getMe()?.role !== 'worker') { link.dataset.emailConfirm='true';void sendingSettings().catch(()=>{}) }
  let pending = false
  const open = (e: MouseEvent): void => {
    if(e.defaultPrevented||!isSendPress(e))return
    if(pending||link.getAttribute('aria-disabled')==='true'||!link.getAttribute('href')){e.preventDefault();return}
    const send = prepare();if(!send?.href){e.preventDefault();return}
    if(onAPhone() || getMe()?.role === 'worker'&&!emailLinks(send.href)){link.setAttribute('href',send.href);send.sent();return}
    link.setAttribute('href',send.href)
    e.preventDefault();e.stopPropagation();pending=true
    const done = (): void => {pending=false;settled?.()}
    openOwnSend({...send,name:send.name ?? link.getAttribute('aria-label')?.split(': ').slice(1).join(': '),sent:()=>{const connected=link.isConnected;done();if(connected)send.sent()}},done,true)
  }
  link.addEventListener('click',open);link.addEventListener('auxclick',open)
}
export function howYouSendCard(): HTMLElement {
  const card = el('section',{class:'ask-ahead-card sending-card'},el('h3',{},'How you send'))
  const draw = (row: Sending): void => {
    const label = choices.find(c=>c[0]===row.email_app)
    const change = el('button',{type:'button',class:'link-button'},'Change')
    change.addEventListener('click',()=>chooseEmail(()=>{void sendingSettings().then(draw)}))
    const sw = el('button',{type:'button',role:'switch',class:`mb-switch${row.confirm_computer_send?' mb-switch-on':''}`,'aria-checked':String(row.confirm_computer_send),'aria-label':'Ask "Did it send?" on a computer'},el('span',{class:'mb-switch-knob'}))
    sw.addEventListener('click',()=>{sw.disabled=true;void saveSending({confirm_computer_send:!row.confirm_computer_send}).then(draw).catch(()=>{sw.disabled=false;toast("Couldn't save. Check your signal and try again.")})})
    card.replaceChildren(el('h3',{},'How you send'),el('div',{class:'sending-setting'},el('span',{},`Email · ${label?label[1]+(['gmail','outlook','yahoo'].includes(label[0])?' in your browser':''):'Choose when you first send'}`),change),el('div',{class:'sending-setting'},el('span',{},el('strong',{},'Ask "Did it send?" on a computer'),el('small',{class:'muted'},'Nothing is counted as sent until you say yes')),sw))
  }
  void sendingSettings().then(draw).catch(()=>card.append(el('p',{class:'muted'},'Check your signal to change how you send.')))
  return card
}
